import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, KeyRound, Power, Trash2 } from "lucide-react";
import { ModuleLinks } from "@/components/ModuleLinks";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, StatusPill, KpiCard, EmptyState } from "@/components/Bits";
import { inr, inrShort, num, dmy, dmyTime, BUSINESS_TYPE_LABEL, titleize } from "@/lib/format";
import { Modal, primaryBtnCls } from "@/components/Kit";

export default function BusinessDetail() {
  const { businessId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/owner/businesses/${businessId}`);
      setData(res.data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = async () => {
    const next = data.business.status === "active" ? "suspended" : "active";
    setBusy("status");
    try {
      await api.post(`/owner/businesses/${businessId}/status`, { status: next, reason: "Owner action" });
      toast.success(`Business ${next}.`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const resetAdmin = async () => {
    setBusy("reset");
    try {
      const { data: res } = await api.post(`/owner/businesses/${businessId}/reset-admin-password`);
      toast.success(res.message);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const exportData = async () => {
    setBusy("export");
    try {
      const response = await api.get(`/owner/businesses/${businessId}/backup`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.business.code}-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded and the export was audited.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const deleteBusiness = async () => {
    setBusy("delete");
    try {
      await api.delete(`/owner/businesses/${businessId}`, { data: { business_name: deleteName } });
      toast.success("Business permanently deleted.");
      window.location.assign("/owner/businesses");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!data) return <Loading label="Loading business dashboard…" />;

  const { business: b, stats, staff, activity, alerts, notifications } = data;
  const isHospital = b.modules.includes("hospital");
  const isPharmacy = b.modules.includes("pharmacy");
  const btn = "inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 hover:border-slate-900 hover:text-slate-900 disabled:opacity-60";

  return (
    <div data-testid="business-detail-page">
      <Link to="/owner/businesses" className="inline-flex items-center gap-2 text-sm text-slate-500 mb-6 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> All businesses
      </Link>
      <PageHeader overline={BUSINESS_TYPE_LABEL[b.business_type]} title={b.name}
        subtitle={`Business ID ${b.code} · ${[b.city, b.state].filter(Boolean).join(", ") || "Location not set"}`}
        actions={
          <>
            <button onClick={resetAdmin} disabled={busy === "reset"} className={btn} data-testid="reset-admin-btn">
              <KeyRound className="h-4 w-4" /> Reset admin password
            </button>
            <button onClick={exportData} disabled={busy === "export"} className={btn} data-testid="export-business-btn">
              <Download className="h-4 w-4" /> Backup / export
            </button>
            <button onClick={toggleStatus} disabled={busy === "status"} data-testid="toggle-business-status-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#1E293B] hover:text-white">
              <Power className="h-4 w-4" /> {b.status === "active" ? "Suspend" : "Activate"}
            </button>
            <button onClick={() => { setDeleteName(""); setDeleteOpen(true); }} className={btn}
              data-testid="delete-business-btn">
              <Trash2 className="h-4 w-4 text-red-600" /> Delete business
            </button>
          </>
        } />

      <div className="panel p-5 mb-6"><ModuleLinks business={b} onChange={load} />
        <Link to="/owner/groups" data-testid="business-manage-group" className="mt-4 block text-sm text-orange-700">{b.group_id ? "Manage linked businesses" : "Link an existing business"}</Link>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Status" value={<StatusPill status={b.status} />} testid="detail-status" onClick={() => {}} />
        <KpiCard label="Staff & users" value={num(stats.staff)} testid="detail-staff" onClick={() => {}} />
        <KpiCard label="Total revenue" value={inrShort(stats.hospital_revenue + stats.pharmacy_revenue)} tone="accent" testid="detail-revenue" onClick={() => {}} />
        <KpiCard label="Created" value={dmy(b.created_at)} testid="detail-created" onClick={() => {}} />
      </div>

      {isHospital && (
        <section className="mt-10">
          <p className="overline text-[#EA580C] mb-4">Hospital operations</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <KpiCard label="Patients" value={num(stats.patients)} onClick={() => {}} testid="detail-patients" />
            <KpiCard label="Appointments" value={num(stats.appointments)} onClick={() => {}} testid="detail-appointments" />
            <KpiCard label="OPD visits" value={num(stats.opd_visits)} onClick={() => {}} testid="detail-opd" />
            <KpiCard label="Admissions" value={num(stats.admissions)} onClick={() => {}} testid="detail-admissions" />
            <KpiCard label="Hospital revenue" value={inrShort(stats.hospital_revenue)} tone="accent" onClick={() => {}} testid="detail-hospital-revenue" />
          </div>
        </section>
      )}

      {isPharmacy && (
        <section className="mt-10">
          <p className="overline text-[#EA580C] mb-4">Pharmacy operations</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            <KpiCard label="Medicines" value={num(stats.medicines)} onClick={() => {}} testid="detail-medicines" />
            <KpiCard label="Invoices" value={num(stats.sales_count)} onClick={() => {}} testid="detail-sales" />
            <KpiCard label="Purchases" value={num(stats.purchases_count)} onClick={() => {}} testid="detail-purchases" />
            <KpiCard label="Low stock" value={num(alerts.low_stock)} tone="warning" onClick={() => {}} testid="detail-low-stock" />
            <KpiCard label="Expiring / expired" value={`${num(alerts.expiring)} / ${num(alerts.expired)}`} tone="danger" onClick={() => {}} testid="detail-expiry" />
            <KpiCard label="Pharmacy revenue" value={inrShort(stats.pharmacy_revenue)} tone="accent" onClick={() => {}} testid="detail-pharmacy-revenue" />
          </div>
        </section>
      )}

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <div className="panel">
          <div className="px-6 py-5 border-b border-slate-200">
            <h3 className="font-ui font-bold tracking-tight">Staff & users</h3>
          </div>
          {staff.length === 0 ? <EmptyState title="No staff yet" subtitle="The administrator can create staff after signing in." testid="staff-empty" /> : (
            <div className="divide-y divide-slate-100">
              {staff.map((s) => (
                <div key={s.user_id} className="px-6 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{s.name}</p>
                    <p className="text-xs text-slate-500 truncate">{s.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-slate-500">{titleize(s.role)}</p>
                    <StatusPill status={s.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="px-6 py-5 border-b border-slate-200">
            <h3 className="font-ui font-bold tracking-tight">Recent activity & audit</h3>
          </div>
          {activity.length === 0 ? <EmptyState title="No activity recorded yet" testid="activity-empty" /> : (
            <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
              {activity.map((a) => (
                <div key={a.log_id} className="px-6 py-4">
                  <p className="text-sm text-slate-900">{titleize(a.action)}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {a.user_email || "system"} · {dmyTime(a.created_at)} · {a.result}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="panel mt-6">
        <div className="px-6 py-5 border-b border-slate-200">
          <h3 className="font-ui font-bold tracking-tight">Business settings & information</h3>
        </div>
        <div className="p-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 text-sm">
          {[["Business ID", b.code], ["Type", BUSINESS_TYPE_LABEL[b.business_type]],
            ["Modules", b.modules.join(", ")], ["Phone", b.phone || "—"],
            ["Email", b.email || "—"], ["GSTIN", b.gstin || "—"],
            ["Drug licence", b.drug_licence || "—"],
            ["Expiry warning", `${b.expiry_warning_days || 90} days`],
            ["Address", b.address || "—"]].map(([k, v]) => (
            <div key={k}>
              <p className="overline text-slate-400">{k}</p>
              <p className="mt-1.5 text-slate-800 break-words">{v}</p>
            </div>
          ))}
        </div>
      </div>

      {notifications.length > 0 && (
        <div className="panel mt-6">
          <div className="px-6 py-5 border-b border-slate-200">
            <h3 className="font-ui font-bold tracking-tight">Notifications</h3>
          </div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {notifications.map((n) => (
              <div key={n.notification_id} className="px-6 py-4">
                <p className="text-sm text-slate-900">{n.title}</p>
                <p className="text-xs text-slate-500 mt-1">{n.body} · {dmyTime(n.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Permanently delete business"
        subtitle="This cannot be undone." testid="delete-business-modal">
        <p className="text-sm text-slate-600">
          Delete <b>{b.name}</b> ({b.code}) with enabled modules: {b.modules.join(", ") || "none"}.
          Tenant data and user access will be removed; audit history is retained.
        </p>
        <label className="mt-5 block text-sm font-medium text-slate-800">
          Type the exact business name to confirm
          <input className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={deleteName} data-testid="delete-business-confirm-input"
            onChange={(e) => setDeleteName(e.target.value)} />
        </label>
        <button type="button" disabled={busy === "delete" || deleteName !== b.name}
          onClick={deleteBusiness} data-testid="delete-business-confirm-btn"
          className={`${primaryBtnCls} mt-5 !bg-red-700 hover:!bg-red-800`}>
          {busy === "delete" ? "Deleting…" : "Permanently delete"}
        </button>
      </Modal>
    </div>
  );
}
